import { Injectable } from '@angular/core';
import { BasicInsuranceModel, BasicInsurancePlan, GenderOrPrenatal } from '../../../api';
import { TrackingService } from '../tracking.service';
import { ECOMMERCE_EVENT_TYPES, Item } from './tracking-ecommerce.events';
import { SupplementaryInsurancesData } from '../../../../pages/supplementary-insurance/supplementary-insurance-selection-page/supplementary-insurance.mapper';
import { CURRENCY_CHF } from './tracking-ecommerce.map';
import { SupplementaryInsuranceTypes } from '../../../states/offer/offer.state';
import { mapBasicInsuranceItem, mapBasicInsuranceItemByModel } from './tracking-ecommerce-basic-insurance.map';
import {
  mapSupplementaryInsuranceItem,
  mapSupplementaryInsuranceItems,
  mapSupplementaryInsurancesOfProduct,
} from './tracking-ecommerce-supplementary-insurance.map';

/* eslint-disable @typescript-eslint/naming-convention */
// Justified class-wide because we have to work with Google Analytics' tag names

@Injectable({
  providedIn: 'root',
})
export class TrackingEcommerceService {
  // VIEW ITEM LIST
  // BASIC + SUPPLEMENTARY
  public trackBasicInsuranceViewItemListEvent(
    basicInsurancePlans: BasicInsurancePlan[],
    franchise: string,
    includesAccidentCoverage: boolean,
    gender: GenderOrPrenatal,
    cartValue: number,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM_LIST,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: basicInsurancePlans.map((basicInsurancePlan) => {
          let { item_name, item_category, item_category2, item_category3, item_variant, price, quantity } =
            mapBasicInsuranceItem(basicInsurancePlan, franchise, includesAccidentCoverage, gender);
          return { item_name, item_category, item_category2, item_category3, item_variant, price, quantity };
        }),
      },
    });
  }

  public trackSupplementaryInsuranceViewItemListEvent(
    supplementaryInsuranceData: SupplementaryInsurancesData,
    gender: GenderOrPrenatal,
    visibleProducts: SupplementaryInsuranceTypes[],
    cartValue: number,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM_LIST,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: mapSupplementaryInsuranceItems(supplementaryInsuranceData, gender, visibleProducts).map((item) => {
          let { item_name, item_category, item_category2, item_category3, item_variant, price, quantity } = item;
          return { item_name, item_category, item_category2, item_category3, item_variant, price, quantity };
        }),
      },
    });
  }

  // SELECT ITEM
  // BASIC + SUPPLEMENTARY
  public trackSelectItemEvent(
    basicInsurancePlan: BasicInsurancePlan,
    franchise: string,
    includesAccidentCoverage: boolean,
    gender: GenderOrPrenatal,
    cartValue: number,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.SELECT_ITEM,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: [basicInsurancePlan].map((plan) => {
          let { item_name, item_category, item_category2, item_category3, item_variant, price, quantity } =
            mapBasicInsuranceItem(plan, franchise, includesAccidentCoverage, gender);
          return { item_name, item_category, item_category2, item_category3, item_variant, price, quantity };
        }),
      },
    });
  }

  public trackSupplementaryInsuranceSelectItemListEvent(
    product: SupplementaryInsuranceTypes,
    supplementaryInsuranceData: SupplementaryInsurancesData,
    gender: GenderOrPrenatal,
    cartValue: number,
  ): void {
    let { item_name, item_category, item_category2, item_category3, item_variant, price, quantity } =
      mapSupplementaryInsuranceItem(product, supplementaryInsuranceData[`${product}LowestPrice`] as number, gender);
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.SELECT_ITEM,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: [{ item_name, item_category, item_category2, item_category3, item_variant, price, quantity }],
      },
    });
  }

  // VIEW ITEM
  // BASIC + SUPPLEMENTARY
  public trackBasicInsuranceViewItemEvent(
    basicInsurancePlan: BasicInsurancePlan,
    franchise: string,
    includesAccidentCoverage: boolean,
    gender: GenderOrPrenatal,
    cartValue: number,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: basicInsurancePlan.models.map((model) => {
          let { item_id, item_name, item_category, item_category2, item_category3, item_variant, price, quantity } =
            mapBasicInsuranceItemByModel(model, franchise, includesAccidentCoverage, gender);
          return { item_id, item_name, item_category, item_category2, item_category3, item_variant, price, quantity };
        }),
      },
    });
  }

  public trackSupplementaryInsuranceViewItemEvent(
    product: SupplementaryInsuranceTypes,
    supplementaryInsuranceData: SupplementaryInsurancesData,
    gender: GenderOrPrenatal,
    cartValue: number,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: mapSupplementaryInsurancesOfProduct(product, supplementaryInsuranceData, gender).map((item) => {
          let { item_id, item_name, item_category, item_category2, item_category3, item_variant, price, quantity } =
            item;
          return { item_id, item_name, item_category, item_category2, item_category3, item_variant, price, quantity };
        }),
      },
    });
  }

  // ADD TO CART
  // BASIC + SUPPLEMENTARY
  public trackBasicInsuranceAddToCartEvent(
    basicInsuranceModel: BasicInsuranceModel,
    franchise: string,
    includesAccidentCoverage: boolean,
    gender: GenderOrPrenatal,
    cartValue: number,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.ADD_TO_CART,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: [basicInsuranceModel].map((_) => {
          let { item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity } =
            mapBasicInsuranceItemByModel(basicInsuranceModel, franchise, includesAccidentCoverage, gender);
          return { item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity };
        }),
      },
    });
  }

  public trackSupplementaryInsuranceAddToCartEvent(product: Item, cartValue: number): void {
    let { item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity } = product;
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.ADD_TO_CART,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: [{ item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity }],
      },
    });
  }

  // REMOVE FROM CART
  // BASIC + SUPPLEMENTARY
  public trackBasicInsuranceRemoveFromCartEvent(
    basicInsuranceModel: BasicInsuranceModel,
    franchise: string,
    includesAccidentCoverage: boolean,
    gender: GenderOrPrenatal,
    cartValue: number,
  ): void {
    let { item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity } =
      mapBasicInsuranceItemByModel(basicInsuranceModel, franchise, includesAccidentCoverage, gender);
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.REMOVE_FROM_CART,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: [{ item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity }],
      },
    });
  }

  public trackSupplementaryInsuranceRemoveFromCartEvent(product: Item, cartValue: number): void {
    let { item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity } = product;
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.REMOVE_FROM_CART,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: cartValue,
        items: [{ item_name, item_id, item_category, item_category2, item_category3, item_variant, price, quantity }],
      },
    });
  }

  // VIEW CART
  public trackViewCartEvent(summaryPrice: number, products: Item[]): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_CART,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: summaryPrice,
        items: products.map((product) => {
          let {
            item_name,
            item_id,
            item_category,
            item_category2,
            item_category3,
            item_category4,
            item_variant,
            price,
            quantity,
          } = product;
          return {
            item_name,
            item_id,
            item_category,
            item_category2,
            item_category3,
            item_category4,
            item_variant,
            price,
            quantity,
          };
        }),
      },
    });
  }

  // BEGIN CHECKOUT
  // CLOSING / QUOTE
  public trackBeginCheckoutClosing(products: Item[]): void {
    this.trackAllInfosWithEventAndShippingTier(products, ECOMMERCE_EVENT_TYPES.BEGIN_CHECKOUT, 'Closing');
  }

  public trackBeginCheckoutQuote(products: Item[]): void {
    this.trackAllInfosWithEventAndShippingTier(products, ECOMMERCE_EVENT_TYPES.BEGIN_CHECKOUT, 'Quote');
  }

  // ADD SHIPPING INFO
  public trackShippingInfo(products: Item[], shippingTier: string): void {
    this.trackAllInfosWithEventAndShippingTier(products, ECOMMERCE_EVENT_TYPES.ADD_SHIPPING_INFO, shippingTier);
  }

  private trackAllInfosWithEventAndShippingTier(products: Item[], event: string, shippingTier: string): void {
    let totalPrice = products.map((product) => product.price).reduce((a, b) => a + b, 0);
    TrackingService.trackEvent({
      event: event,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: totalPrice,
        items: products.map((item) => {
          let {
            item_name,
            item_id,
            item_category,
            item_category2,
            item_category3,
            item_category4,
            item_category5,
            item_variant,
            price,
            quantity,
            item_brand,
            contract_length,
          } = item;
          return {
            item_name,
            item_id,
            item_category,
            item_category2,
            item_category3,
            item_category4,
            item_category5,
            item_variant,
            price,
            quantity,
            shipping_tier: shippingTier,
            item_brand,
            contract_length,
          };
        }),
      },
    });
  }
}
